import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import './OrderDetails.css';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadOrderDetails();
  }, [id]);

  const loadOrderDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ORDERS, getFetchConfig('GET', null, true));
      
      if (response.ok) {
        const data = await response.json();
        const foundOrder = data.orders?.find(o => o.id.toString() === id);
        if (foundOrder) {
          setOrder(foundOrder);
        } else {
          setMessage('Pedido não encontrado');
        }
      } else {
        setMessage('Erro ao carregar pedido');
      }
    } catch (error) {
      setMessage('Erro ao carregar pedido: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9500';
      case 'processando': return '#007bff';
      case 'enviado': return '#17a2b8';
      case 'entregue': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const calculateTotal = () => {
    if (!order || !order.items) return 0;
    return order.totalAmount || order.items.reduce((total, item) => total + (item.itemTotal || item.productPrice * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="order-details-container">
        <div className="loading">Carregando detalhes do pedido...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-container">
        <div className="error-message">{message}</div>
        <button 
          onClick={() => navigate('/orders')} 
          className="btn btn-secondary"
        >
          Voltar aos Meus Pedidos
        </button>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button 
          onClick={() => navigate('/orders')} 
          className="btn btn-secondary"
        >
          ← Voltar aos Meus Pedidos
        </button>
        <h1>Detalhes do Pedido #{order.id}</h1>
      </div>

      <div className="order-details-content">
        {/* Informações do Cliente */}
        <div className="info-section">
          <h2>Suas Informações</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nome:</label>
              <span>{order.userId || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{order.userEmail || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Telefone:</label>
              <span>{'N/A'}</span>
            </div>
            <div className="info-item full-width">
              <label>Endereço de Entrega:</label>
              <span>{'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Itens do Pedido */}
        <div className="info-section">
          <h2>Itens do Seu Pedido</h2>
          <div className="items-list">
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <div key={index} className="item-card">
                  <div className="item-info">
                    <h4>{item.productName}</h4>
                    <p>Quantidade: {item.quantity}</p>
                    <p>Preço unitário: €{item.productPrice?.toFixed(2)}</p>
                    <p className="item-total">Subtotal: €{item.itemTotal?.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p>Nenhum item encontrado</p>
            )}
          </div>
          
          <div className="order-total">
            <h3>Total do Pedido: €{calculateTotal().toFixed(2)}</h3>
          </div>
        </div>

        {/* Status e Datas */}
        <div className="info-section">
          <h2>Status do Pedido</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Status Atual:</label>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {order.status?.toUpperCase() || 'PENDENTE'}
              </span>
            </div>
            <div className="info-item">
              <label>Data do Pedido:</label>
              <span>
                {order.createdAt ? new Date(order.createdAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <label>Última Atualização:</label>
              <span>
                {order.updatedAt ? new Date(order.updatedAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Informação sobre o status */}
        <div className="info-section">
          <h2>Sobre o Seu Pedido</h2>
          <div className="info-message">
            {order.status === 'pendente' && (
              <p>O seu pedido foi recebido e está sendo processado. Entraremos em contacto em breve.</p>
            )}
            {order.status === 'processando' && (
              <p>O seu pedido está sendo preparado para envio. Será notificado quando for enviado.</p>
            )}
            {order.status === 'enviado' && (
              <p>O seu pedido foi enviado! Deve recebê-lo em breve no endereço fornecido.</p>
            )}
            {order.status === 'entregue' && (
              <p>O seu pedido foi entregue com sucesso! Obrigado pela sua compra.</p>
            )}
            {order.status === 'cancelado' && (
              <p>O seu pedido foi cancelado. Se tiver dúvidas, contacte-nos.</p>
            )}
            <p>Se tiver alguma dúvida sobre o seu pedido, pode contactar-nos através do email ou telefone fornecidos.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
